import clsx from 'clsx'
import { IconNames } from 'lib/settings/icons'
import { Fragment } from 'react'
import Icon from '../Icon/Icon'

export interface DeliveryStepsProps {
  /**
   * The steps to display
   */
  steps: string[]
  /**
   * The current step to display
   */
  currentStep?: string
  /**
   * Whether the delivery is delayed
   */
  isDelayed?: boolean
  /**
   * Text to display when the delivery is delayed
   */
  delayedText?: string
  /**
   * Whether the delivery is cancelled
   */
  isCancelled?: boolean
}

export default function DeliverySteps({
  steps,
  currentStep,
  isDelayed,
  delayedText = 'Delayed',
  isCancelled,
}: DeliveryStepsProps) {
  const currentStepIndex = steps.findIndex((step) => step === currentStep)
  const lastIndex = steps.length - 1

  return (
    <div>
      <div className="flex items-start justify-between gap-x-2">
        {steps.map((step, index) => (
          <Fragment key={step}>
            {/* Line */}
            {index > 0 ? (
              <span
                className={clsx(
                  'border-t flex-1 mt-3 lg:mt-4',
                  index <= currentStepIndex
                    ? 'border-theme-accessibility'
                    : 'border-neutral-500',
                  isCancelled && '[&]:border-neutral-500',
                )}
              ></span>
            ) : null}
            {/* Number/Icon and Label */}
            <div
              key={step}
              className={clsx(
                'flex flex-col',
                index > currentStepIndex
                  ? 'w-6 lg:w-8'
                  : index === currentStepIndex && isDelayed
                    ? 'w-6'
                    : 'w-6 lg:w-8',
                index === 0
                  ? 'items-start'
                  : index === lastIndex
                    ? 'items-end'
                    : 'items-center',
              )}
            >
              {/* Number/Icon */}
              <Icon
                icon={
                  index > currentStepIndex
                    ? (`number-${index + 1}-circle` as IconNames)
                    : index === currentStepIndex && isDelayed
                      ? 'warning-triangle-filled'
                      : index === currentStepIndex && isCancelled
                        ? 'cancel'
                        : 'check-circle-filled'
                }
                className={clsx(
                  'h-6 lg:h-8',
                  index > currentStepIndex
                    ? 'w-6 lg:w-8 text-neutral-600'
                    : index === currentStepIndex && isDelayed
                      ? 'w-6 text-neutral-700'
                      : index === currentStepIndex && isCancelled
                        ? 'w-6 lg:w-8 text-neutral-500'
                        : 'w-6 lg:w-8 text-theme-accessibility',
                )}
              />
              {/* Label */}
              <span
                key={step}
                className={clsx(
                  'type-body-6 whitespace-nowrap mt-2',
                  index <= currentStepIndex
                    ? 'text-neutral-700'
                    : 'text-neutral600',
                )}
              >
                {index === currentStepIndex && isDelayed ? delayedText : step}
              </span>
            </div>
          </Fragment>
        ))}
      </div>
    </div>
  )
}
